import classNames from 'classnames'

import InstagramEmbed from '~/components/atoms/InstagramEmbed/InstagramEmbed'
import Spacer, { SpacerSizes } from '~/components/atoms/Spacer/Spacer'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import VideoEmbed from '~/components/atoms/VideoEmbed/VideoEmbed'

import styles from './MediaEmbed.module.css'

type MediaEmbedType =
  | 'soundcloud'
  | 'twitter'
  | 'instagram'
  | 'facebook'
  | 'youtube'
  | 'vimeo'

export interface MediaEmbedProps {
  testID?: string
  type: MediaEmbedType
  source: 'string'
  mediaCaption: string
}

export default function MediaEmbed({
  testID,
  source,
  type,
  mediaCaption,
}: MediaEmbedProps) {
  if (!source) return null

  const caption = mediaCaption && (
    <Text
      className={classNames(styles.mediaCaption)}
      size={TextSizes.magSecondaryBodySml}
    >
      {mediaCaption}
    </Text>
  )

  switch (type) {
    case 'soundcloud':
      // Remove old brand color
      // eslint-disable-next-line no-case-declarations
      let cleanSource = source.replace('&color=%2300e5a3', '')

      cleanSource = `${cleanSource}&show_teaser=false&color=%23000000`

      return (
        <div className={styles.base} data-testid={testID}>
          <Spacer size={SpacerSizes.md} />
          <iframe
            src={`${cleanSource}`}
            width="100%"
            title="Media embed"
          ></iframe>
          {caption}
          <Spacer size={SpacerSizes.md} />
        </div>
      )

    case 'twitter':
      return (
        <div className={styles.base} data-testid={testID}>
          <Spacer size={SpacerSizes.md} />
          <blockquote className="twitter-tweet">
            <a href={source}></a>
          </blockquote>
          <script async src="https://platform.twitter.com/widgets.js"></script>
          {caption}
          <Spacer size={SpacerSizes.md} />
        </div>
      )

    case 'instagram':
      return (
        <div className={styles.base} data-testid={testID}>
          <Spacer size={SpacerSizes.md} />
          <InstagramEmbed source={source} caption={mediaCaption} />
          <Spacer size={SpacerSizes.md} />
        </div>
      )

    case 'facebook':
      return (
        <div className={classNames(styles.base)}>
          <div
            className={'fb-post'}
            data-href={source}
            data-width="750"
            data-show-text="true"
            data-testid={testID}
          >
            <blockquote
              cite={source}
              className="fb-xfbml-parse-ignorwee"
            ></blockquote>
          </div>
          {caption}
        </div>
      )
    case 'youtube': {
      return (
        <div className={styles.base} data-testid={testID}>
          <Spacer size={SpacerSizes.md} />
          <VideoEmbed videoSrc={source} videoType="youtube" />
          {caption}
          <Spacer size={SpacerSizes.md} />
        </div>
      )
    }

    case 'vimeo': {
      return (
        <div className={styles.base} data-testid={testID}>
          <Spacer size={SpacerSizes.md} />
          <VideoEmbed videoSrc={source} videoType="vimeo" />
          {caption}
          <Spacer size={SpacerSizes.md} />
        </div>
      )
    }
    default: {
      return (
        <div className={styles.base} data-testid={testID}>
          <iframe src={`${source}`} title="Media embed"></iframe>
          {caption}
          <Spacer size={SpacerSizes.md} />
        </div>
      )
    }
  }
}
